<script setup lang="ts">
import { defineProps, ref, withDefaults } from "vue";
import { Feature } from "../../classes/feature";
import { ProductGroup } from "../../classes/productGroup";

interface Props {
  productGroup: ProductGroup;
  feature: Feature;
  defaultProduct: number | null;
  highlightDefaultProduct: boolean;
  liability: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  productGroup: null,
  feature: null,
  defaultProduct: null,
  highlightDefaultProduct: false,
  liability: false,
});

const extendedRows = ref<{ [key: string]: boolean }>({});

if (props.productGroup?.products?.length < 3) {
  extendedRows.value = { "43": true };
}

function extendRow(key: number): void {
  extendedRows.value[key] = !extendedRows.value[key];
}
</script>

<!-- FeatureTable.vue -->
<template>
  <div class="table-content-row" :data-cy="'product-row-' + props.feature.id">
    <div
      class="table-content-head"
      @click="
        props.feature.description ? extendRow(props.feature.id) : undefined
      "
      @keyup.enter="
        props.feature.description ? extendRow(props.feature.id) : undefined
      "
      :class="{ 'cursor-pointer': props.feature.description }"
    >
      <div class="table-content-head__name">
        <div v-html="props.feature.value"></div>
        <div
          class="agila-icon-info"
          v-if="props.feature.description"
          tabindex="0"
        ></div>
      </div>
      <div
        class="text-small"
        v-if="props.feature.subValue || extendedRows[props.feature.id]"
        v-html="props.feature.subValue || props.feature.description"
      ></div>
    </div>
    <div
      class="table-content-columns"
      :class="{
        'hps-combi': productGroup?.products?.length < 3,
        'single-product': productGroup?.products?.length === 1,
        liability: props.liability,
      }"
    >
      <div
        class="table-content-column"
        v-for="(product, index) in productGroup.products"
        :key="product.id"
        @click="
          product.getFeatureValue(props.feature.id)?.description
            ? extendRow(props.feature.id)
            : undefined
        "
        @keyup.enter="
          product.getFeatureValue(props.feature.id)?.description
            ? extendRow(props.feature.id)
            : undefined
        "
        :class="{
          'cursor-pointer': product.getFeatureValue(props.feature.id)
            ?.description,
          'non-highlight-column':
            props.highlightDefaultProduct &&
            props.defaultProduct !== 0 &&
            props.defaultProduct !== index + 1,
        }"
      >
        <div class="table-content-column__name">
          <div
            v-if="
              typeof product.getFeatureValue(props.feature.id)?.value ===
              'string'
            "
            v-html="product.getFeatureValue(props.feature.id)?.value"
          ></div>
          <div
            v-else
            :class="{
              'agila-icon-check':
                product.getFeatureValue(props.feature.id)?.value === true,
              'agila-icon-skip':
                product.getFeatureValue(props.feature.id)?.value === false,
            }"
          ></div>
          <div
            v-if="product.getFeatureValue(props.feature.id)?.description"
            class="agila-icon-info"
            tabindex="0"
          ></div>
        </div>
        <div
          class="text-small"
          v-if="
            product.getFeatureValue(props.feature.id)?.subValue ||
            extendedRows[props.feature.id]
          "
          v-html="
            product.getFeatureValue(props.feature.id)?.subValue ||
            product.getFeatureValue(props.feature.id)?.description
          "
        ></div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "../../styles/variables";

.table-content-columns {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  color: var(--color-gray-900);

  &.liability {
    grid-template-columns: 1fr 1fr;
  }

  &.hps-combi {
    grid-template-columns: 1fr 1fr;

    .table-content-column {
      border-right: 0;
    }
  }

  &.single-product {
    grid-template-columns: 1fr;

    .table-head-column-con {
      border-right: 0 !important;
    }
  }

  .table-content-column {
    font-family: var(--font-roboto-bold), sans-serif;
    background-color: var(--color-blue-100);
    padding: 16px 8px;
    justify-content: center;
    overflow: auto;

    &.non-highlight-column {
      background-color: var(--color-blue-50);
    }

    &__name {
      justify-content: center;
      text-align: center;
      word-break: break-word;
      line-height: 22px;
    }

    .text-small {
      text-align: center;
      overflow-x: hidden;
      text-overflow: ellipsis;
    }

    .agila-icon-info {
      margin-bottom: 2px;

      &:focus-visible {
        box-shadow: 0 0 0 2px var(--color-yellow-300),
          0 0 0 4px var(--color-blue-focus-line) !important;
        background-color: var(--color-yellow-300);
        border-color: var(--color-yellow-300);
        outline: none;
      }
    }
  }

  > div:nth-of-type(2) {
    border-left: 1px solid var(--color-gray-500);
    border-right: 1px solid var(--color-gray-500);
    margin-left: -1px;
    margin-right: -1px;
    z-index: 1;
  }

  @media screen and (min-width: $breakpoint-m) {
    > div {
      border-left: 1px solid var(--color-gray-500);
      border-right: 1px solid var(--color-gray-500);
    }
  }
}
</style>
